import React from 'react';
import { useQuery } from 'react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  EmojiEvents as AwardsIcon,
} from '@mui/icons-material';
import { gebizAPI } from '../../services/api';

const getMatchStatusColor = (status) => {
  switch (status) {
    case 'exact':
      return 'success';
    case 'admin_confirmed':
      return 'primary';
    case 'admin_corrected':
      return 'secondary';
    case 'new_lead':
      return 'warning';
    case 'fuzzy_review':
      return 'info';
    case 'skipped':
      return 'default';
    default:
      return 'default';
  }
};

const formatMatchStatus = (status) => {
  switch (status) {
    case 'exact':
      return 'Exact Match';
    case 'admin_confirmed':
      return 'Admin Confirmed';
    case 'admin_corrected':
      return 'Admin Corrected';
    case 'new_lead':
      return 'New Lead';
    case 'fuzzy_review':
      return 'Fuzzy Review';
    case 'skipped':
      return 'Skipped';
    case 'pending':
      return 'Pending';
    default:
      return status || 'Unknown';
  }
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const LeadAwardsDialog = ({ open, onClose, jobId, lead }) => {
  const { data, isLoading, error } = useQuery(
    ['leadAwards', jobId, lead?.id],
    () => gebizAPI.getLeadAwards(jobId, lead?.id),
    {
      enabled: open && !!lead?.id && !!jobId,
      staleTime: 30000,
    }
  );

  const awards = data?.awards || [];
  const totalAwards = data?.total_awards || 0;
  const totalValue = data?.total_value || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AwardsIcon color="primary" />
            <Typography variant="h6">
              GeBIZ Awards - {lead?.company_name || 'Unknown'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error">
              Failed to load awards: {error.message}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Summary Section */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={`${totalAwards} Award${totalAwards !== 1 ? 's' : ''}`}
                color="primary"
                variant="outlined"
              />
              <Divider orientation="vertical" flexItem />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Total Value: {formatCurrency(totalValue)}
              </Typography>
            </Box>

            {/* Awards Table */}
            {awards.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No awards found for this lead.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Quotation No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Agency</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Award Value</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Awarded Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Match Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {awards.map((award) => (
                      <TableRow key={award.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {award.quotation_no || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 250,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={award.title}
                          >
                            {award.title || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {award.agency_awarding || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatCurrency(award.award_value)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(award.awarded_date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatMatchStatus(award.match_status)}
                            color={getMatchStatusColor(award.match_status)}
                            size="small"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Footer info */}
            {awards.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Showing {awards.length} of {totalAwards} award{totalAwards !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadAwardsDialog;
